import { StepType } from "@redotech/redo-return-ui/flows/step";

/**
 * Enum for drag-and-drop step types in the flow editor
 */
export enum FlowEditorDragType {
  AI_RETURN_REASONS = "AI_RETURN_REASONS",
  CONDITION = "CONDITION",
  MULTIPLE_CHOICE = "MULTIPLE_CHOICE",
  RETURN_REASON = "RETURN_REASON",
  INPUT = "INPUT",
  INFORMATION = "INFORMATION",
  AB_TEST = "AB_TEST",

  REJECT = "REJECT",
  ADD_RETURN_TAG = "ADD_RETURN_TAG",
  BLOCK = "BLOCK",
  RETURN = "RETURN",
  FLAG = "FLAG",
  MANUAL_REVIEW = "MANUAL_REVIEW",
  OVERRIDE_ADJUSTMENT = "OVERRIDE_ADJUSTMENT",
  OVERRIDE_GREEN_RETURN = "OVERRIDE_GREEN_RETURN",
  DYNAMIC_BUNDLE_ADJUSTMENT = "DYNAMIC_BUNDLE_ADJUSTMENT",
  EXCHANGE_SHIPPING_NAME = "EXCHANGE_SHIPPING_NAME",
  DISCOUNT_CODE_NAME = "DISCOUNT_CODE_NAME",
  SUBMIT_CLAIM = "SUBMIT_CLAIM",
  SUBMIT_REPAIR = "SUBMIT_REPAIR",
  SUBMIT_WARRANTY = "SUBMIT_WARRANTY",
  SUBMIT_WARRANTY_REGISTRATION = "SUBMIT_WARRANTY_REGISTRATION",
  SET_RETURN_LOCATIONS = "SET_RETURN_LOCATIONS",
  REDO_MANAGED_LOCATION = "REDO_MANAGED_LOCATION",
  COMBINE_LABEL = "COMBINE_LABEL",
  SEPARATE_LABELS = "SEPARATE_LABELS",
  RETURN_PROCESSING_DECISION = "RETURN_PROCESSING_DECISION",
  EVIDENCE = "EVIDENCE",
  AGENT_VERIFICATION = "AGENT_VERIFICATION",
}

export function stepTypeToDragType(
  stepType: StepType<any, any>,
): FlowEditorDragType | undefined {
  const title = stepType.title.toLowerCase();

  // Match by title
  if (title === "ai return reasons") {
    return FlowEditorDragType.AI_RETURN_REASONS;
  }
  if (title === "condition") {
    return FlowEditorDragType.CONDITION;
  }
  if (title === "multiple choice") {
    return FlowEditorDragType.MULTIPLE_CHOICE;
  }
  if (
    title === "return reason" ||
    title === "claim reason" ||
    title === "warranty reason"
  ) {
    return FlowEditorDragType.RETURN_REASON;
  }
  if (title === "input") {
    return FlowEditorDragType.INPUT;
  }
  if (title === "information") {
    return FlowEditorDragType.INFORMATION;
  }
  if (title === "a/b test" || title === "weighted branch") {
    return FlowEditorDragType.AB_TEST;
  }
  if (title === "reject") {
    return FlowEditorDragType.REJECT;
  }
  if (
    title === "add return tag" ||
    title === "add claim tag" ||
    title === "add warranty tag"
  ) {
    return FlowEditorDragType.ADD_RETURN_TAG;
  }
  if (title === "block") {
    return FlowEditorDragType.BLOCK;
  }
  if (title === "return") {
    return FlowEditorDragType.RETURN;
  }
  if (title === "flag") {
    return FlowEditorDragType.FLAG;
  }
  if (title === "mark for manual review") {
    return FlowEditorDragType.MANUAL_REVIEW;
  }
  if (title === "override adjustment") {
    return FlowEditorDragType.OVERRIDE_ADJUSTMENT;
  }
  if (title === "require send back") {
    return FlowEditorDragType.OVERRIDE_GREEN_RETURN;
  }
  if (title === "dynamic bundle adjustment") {
    return FlowEditorDragType.DYNAMIC_BUNDLE_ADJUSTMENT;
  }
  if (title === "shipping name") {
    return FlowEditorDragType.EXCHANGE_SHIPPING_NAME;
  }
  if (title === "discount code name") {
    return FlowEditorDragType.DISCOUNT_CODE_NAME;
  }
  if (title === "submit claim") {
    return FlowEditorDragType.SUBMIT_CLAIM;
  }
  if (title === "submit repair") {
    return FlowEditorDragType.SUBMIT_REPAIR;
  }
  if (title === "submit warranty registration") {
    return FlowEditorDragType.SUBMIT_WARRANTY_REGISTRATION;
  }
  if (title === "submit warranty") {
    return FlowEditorDragType.SUBMIT_WARRANTY;
  }
  if (title === "location") {
    return FlowEditorDragType.SET_RETURN_LOCATIONS;
  }
  if (title === "returns center" || title === "redo returns center") {
    return FlowEditorDragType.REDO_MANAGED_LOCATION;
  }
  if (title === "combine label") {
    return FlowEditorDragType.COMBINE_LABEL;
  }
  if (title === "separate labels") {
    return FlowEditorDragType.SEPARATE_LABELS;
  }
  if (title === "decision") {
    return FlowEditorDragType.RETURN_PROCESSING_DECISION;
  }
  if (title === "capture evidence") {
    return FlowEditorDragType.EVIDENCE;
  }
  if (title === "agent verification") {
    return FlowEditorDragType.AGENT_VERIFICATION;
  }

  return undefined;
}

/**
 * Maps a FlowEditorDragType to its corresponding StepType from the available stepOptions
 */
export function dragTypeToStepType(
  dragType: FlowEditorDragType,
  stepOptions: StepType<any, any>[],
): StepType<any, any> | undefined {
  return stepOptions.find(
    (stepType) => stepTypeToDragType(stepType) === dragType,
  );
}

/**
 * Categorizes step types into "Steps" and "Actions" sections
 */
export interface StepCategory {
  name: string;
  steps: { stepType: StepType<any, any>; dragType: FlowEditorDragType }[];
}

export function categorizeSteps(
  stepOptions: StepType<any, any>[],
  actionsSectionName = "Actions",
): StepCategory[] {
  const stepsSection: StepCategory["steps"] = [];
  const actionsSection: StepCategory["steps"] = [];

  const stepsDragTypes = new Set([
    FlowEditorDragType.AI_RETURN_REASONS,
    FlowEditorDragType.CONDITION,
    FlowEditorDragType.MULTIPLE_CHOICE,
    FlowEditorDragType.RETURN_REASON,
    FlowEditorDragType.INPUT,
    FlowEditorDragType.INFORMATION,
    FlowEditorDragType.AB_TEST,
    FlowEditorDragType.RETURN_PROCESSING_DECISION,
  ]);

  for (const stepType of stepOptions) {
    const dragType = stepTypeToDragType(stepType);
    if (!dragType) {
      continue;
    }

    if (stepsDragTypes.has(dragType)) {
      stepsSection.push({ stepType, dragType });
    } else {
      actionsSection.push({ stepType, dragType });
    }
  }

  const categories: StepCategory[] = [];

  if (stepsSection.length > 0) {
    categories.push({ name: "Steps", steps: stepsSection });
  }

  if (actionsSection.length > 0) {
    categories.push({ name: actionsSectionName, steps: actionsSection });
  }

  return categories;
}
